import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Users, Search, Heart, Gift, ArrowDown, ArrowRight } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Customer {
  id: string
  full_name: string
  email: string
  referral_code: string
  visit_stamps: number
  referral_stamps: number
  referred_by?: string
  created_at: string
  children?: Customer[]
  referralLeads?: Array<{
    id: string
    first_name: string
    last_name: string
    email: string
    status: string
    created_at: string
    converted_to_customer_id?: string
  }>
}

interface ReferralMindmapProps {
  tenantId: string
}

const ReferralMindmap = ({ tenantId }: ReferralMindmapProps) => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  // New state for statistics calculation
  const [allCustomers, setAllCustomers] = useState<Customer[]>([])
  const [allReferralLeads, setAllReferralLeads] = useState<any[]>([])
  const { toast } = useToast()

  useEffect(() => {
    fetchReferralData()
  }, [tenantId])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCustomers(customers)
    } else {
      const filtered = customers.filter(customer =>
        customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredCustomers(filtered)
    }
  }, [customers, searchTerm])

  const fetchReferralData = async () => {
    try {
      setLoading(true)
      
      // Fetch customers and their referral relationships
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('id, full_name, email, referral_code, visit_stamps, referral_stamps, referred_by, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: true })

      if (customersError) throw customersError

      // Also fetch referral leads to show all referral attempts
      const { data: referralLeads, error: leadsError } = await supabase
        .from('referral_leads')
        .select(`
          id,
          first_name,
          last_name,
          email,
          status,
          created_at,
          referrer_customer_id,
          referrer_code,
          converted_to_customer_id
        `)
        .eq('tenant_id', tenantId)

      if (leadsError) throw leadsError

      // Store all data for statistics
      setAllCustomers(customersData || [])
      setAllReferralLeads(referralLeads || [])

      // Build the tree structure including both customers and referral leads
      const customerMap = new Map<string, Customer>()
      const rootCustomers: Customer[] = []

      // First pass: create all customer objects
      customersData.forEach(customer => {
        const customerWithLeads = {
          ...customer,
          children: [] as Customer[],
          referralLeads: [] as any[]
        }
        
        // Add referral leads for this customer
        const customerLeads = referralLeads.filter(lead => 
          lead.referrer_customer_id === customer.id
        )
        customerWithLeads.referralLeads = customerLeads
        
        customerMap.set(customer.id, customerWithLeads)
      })

      // Second pass: build customer referral tree (only converted customers)
      customersData.forEach(customer => {
        const customerNode = customerMap.get(customer.id)!
        
        if (customer.referred_by) {
          const parent = customerMap.get(customer.referred_by)
          if (parent) {
            parent.children!.push(customerNode)
          } else {
            // Parent not found (maybe from different tenant), treat as root
            rootCustomers.push(customerNode)
          }
        } else {
          rootCustomers.push(customerNode)
        }
      })

      setCustomers(rootCustomers)
    } catch (error) {
      console.error('Error fetching referral data:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungsdaten konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (customerId: string) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId)
    } else {
      newExpanded.add(customerId)
    }
    setExpandedNodes(newExpanded)
  }

  const expandAll = () => {
    const allIds = new Set<string>()
    const collectIds = (customers: Customer[]) => {
      customers.forEach(customer => {
        if (customer.children && customer.children.length > 0) {
          allIds.add(customer.id)
          collectIds(customer.children)
        }
      })
    }
    collectIds(filteredCustomers)
    setExpandedNodes(allIds)
  }

  const collapseAll = () => {
    setExpandedNodes(new Set())
  }

  const renderCustomerNode = (customer: Customer, level: number = 0) => {
    const hasChildren = customer.children && customer.children.length > 0
    const hasReferralLeads = customer.referralLeads && customer.referralLeads.length > 0
    const isExpanded = expandedNodes.has(customer.id)
    const indentLevel = level * 2

    return (
      <div key={customer.id} className="mb-2">
        <div 
          className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${
            level === 0 ? 'bg-primary/5 border-primary/20' : 'bg-background border-border'
          }`}
          style={{ marginLeft: `${indentLevel}rem` }}
        >
          {(hasChildren || hasReferralLeads) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleExpanded(customer.id)}
              className="p-1 h-6 w-6"
            >
              {isExpanded ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </Button>
          )}
          
          {!(hasChildren || hasReferralLeads) && <div className="w-6" />}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium truncate">{customer.full_name}</span>
              <span className="text-sm text-muted-foreground truncate">{customer.email}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-xs">
                Code: {customer.referral_code}
              </Badge>
              
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3 text-primary" />
                <span>{customer.visit_stamps}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <Gift className="h-3 w-3 text-accent" />
                <span>{customer.referral_stamps}</span>
              </div>
              
              {hasChildren && (
                <Badge variant="secondary" className="text-xs">
                  {customer.children!.length} Unterstützer
                </Badge>
              )}
              
              {hasReferralLeads && (
                <Badge variant="outline" className="text-xs">
                  {customer.referralLeads!.length} Empfehlung{customer.referralLeads!.length !== 1 ? 'en' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        {(hasChildren || hasReferralLeads) && isExpanded && (
          <div className="mt-2 ml-4 border-l-2 border-muted pl-4">
            {/* Show customer children first */}
            {hasChildren && customer.children!.map(child => renderCustomerNode(child, level + 1))}
            
            {/* Show referral leads */}
            {hasReferralLeads && customer.referralLeads!.map(lead => (
              <div key={`lead-${lead.id}`} className="mb-2">
                <div 
                  className="flex items-center gap-3 p-3 rounded-lg border bg-accent/5 border-accent/20"
                  style={{ marginLeft: `${(level + 1) * 2}rem` }}
                >
                  <div className="w-6" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="h-4 w-4 text-accent" />
                      <span className="font-medium truncate">{lead.first_name} {lead.last_name}</span>
                      <span className="text-sm text-muted-foreground truncate">{lead.email}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <Badge 
                        variant={lead.status === 'kunde' ? 'default' : 'secondary'} 
                        className="text-xs"
                      >
                        {lead.status === 'kunde' ? 'Unterstützer' : 
                         lead.status === 'neue_empfehlung' ? 'Neue Empfehlung' :
                         lead.status === 'kontaktiert' ? 'Kontaktiert' :
                         lead.status === 'interessiert' ? 'Interessiert' :
                         lead.status === 'nicht_interessiert' ? 'Nicht interessiert' : lead.status}
                      </Badge>
                      
                      <span className="text-xs text-muted-foreground">
                        Empfohlen am {new Date(lead.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <p>Empfehlungsnetzwerk wird geladen...</p>
        </CardContent>
      </Card>
    )
  }

  // Statistics calculations using all data
  const totalCustomers = allCustomers.length
  const totalReferrals = allReferralLeads.length  
  const referralSources = allCustomers.filter(customer => 
    allReferralLeads.some(lead => lead.referrer_customer_id === customer.id)
  ).length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Empfehlungsnetzwerk Mindmap
        </CardTitle>
        <CardDescription>
          Visualisierung der Unterstützerempfehlungen - wer hat wen empfohlen
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-3 bg-primary/5 rounded-lg">
            <div className="text-2xl font-bold text-primary">{totalCustomers}</div>
            <div className="text-sm text-muted-foreground">Unterstützer gesamt</div>
          </div>
          <div className="p-3 bg-accent/5 rounded-lg">
            <div className="text-2xl font-bold text-accent">{totalReferrals}</div>
            <div className="text-sm text-muted-foreground">Empfehlungen gesamt</div>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-2xl font-bold">{referralSources}</div>
            <div className="text-sm text-muted-foreground">Empfehlungsquellen</div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Unterstützer suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={expandAll}>
              Alle erweitern
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAll}>
              Alle einklappen
            </Button>
          </div>
        </div>

        {/* Mindmap */}
        <div className="max-h-[600px] overflow-y-auto">
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Unterstützer gefunden</h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff.' : 'Noch keine Unterstützer vorhanden.'}
              </p>
            </div>
          ) : (
            filteredCustomers.map(customer => renderCustomerNode(customer))
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default ReferralMindmap